import extensions from "_src/engine/extensions";
import FrameBuffer from "_src/engine/framebuffer";
import objFactory from "_src/engine/factories/object";
import primitiveFactory from "_src/engine/factories/primitive";
import {
  cameraType,
  primitiveType,
  blendFactor,
  pixelBufferType,
  RBIntFormat,
  attachmentId
} from "_src/engine/constants";
import Camera from "_src/engine/camera";

import Pass from "_src/render_graph/pass";
import CopyShader from "_src/shaders/copy/copy";

import resourceManager from "_src/render_graph/resource_manager";

const SCALE = 3.0;

export default class ViewCubePass extends Pass {

  constructor(attachmentsStorage) {

    super(attachmentsStorage);

    this.rightOffset = null;
    this.topOffset = null;
    this.screenWidth = null;
    this.screenHeight = null;

    this.savedWidth = null;
    this.savedHeight = null;

    this.init();

  }

  init() {

    const cameraMain = new Camera({type: cameraType.ORTHO});
    const cameraCopy = new Camera();
    const cameraBillboard = new Camera({type: cameraType.ORTHO});

    cameraMain.left   = -SCALE;
    cameraMain.right  =  SCALE;
    cameraMain.top    =  SCALE;
    cameraMain.bottom = -SCALE;

    cameraBillboard.left   = -SCALE;
    cameraBillboard.right  =  SCALE;
    cameraBillboard.top    =  SCALE;
    cameraBillboard.bottom = -SCALE;

    const mainScene = this._addScene(cameraMain);
    mainScene.clearedStencil = false;
    mainScene.clearedDepth = true;
    mainScene.clearedColor = true;

    const copyScene = this._addScene(cameraCopy);
    copyScene.clearedStencil = false;
    copyScene.clearedDepth = false;
    copyScene.clearedColor = false;

    const billboardScene = this._addScene(cameraBillboard);
    billboardScene.clearedStencil = false;
    billboardScene.clearedDepth = false;
    billboardScene.clearedColor = false;

    const copyShader = new CopyShader({transparent : true});
    copyShader.setSourceBlendFactor(blendFactor.ONE);

    const copyScreenPlane = objFactory.createDrawable(copyShader);
    copyScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    copyScene.add(copyScreenPlane);

  }

  setScreenAppearance(_rightOffset, _topOffset, _screenWidth, _screenHeight) {

    const currScreenHeight = this.screenHeight;
    const currScreenWidth = this.screenWidth;

    if (_screenHeight !== this.screenHeight || _screenWidth !== this.screenWidth || _topOffset !== this.topOffset || _rightOffset !== this.rightOffset) {
      this.rightOffset = _rightOffset;
      this.topOffset = _topOffset;
      this.screenWidth = _screenWidth;
      this.screenHeight = _screenHeight;
    } else {
      return;
    }

    if (!this.enabled) {
      return;
    }

    if (_screenHeight !== currScreenHeight || _screenWidth !== currScreenWidth) {
      const mainScene = this.getMainScene();

      if (extensions.isMultisamplingSupported()) {
        const mSamplingBuffer = mainScene.resolvedFramebuffer;
        mSamplingBuffer.resize(_screenWidth * 2, _screenHeight * 2);
      }

      const mainFramebuffer = mainScene.framebuffer;
      mainFramebuffer.resize(_screenWidth * 2, _screenHeight * 2);

      const mainCamera = mainScene.camera;
      mainCamera.setViewport(_screenWidth * 2, _screenHeight * 2);
    }

    this.resize(this.savedWidth, this.savedHeight);

  }


  resize(width, height) {

    const left = width - this.rightOffset;
    const bottom = height - this.topOffset;

    const copyCamera = this.getSecondScene().camera;
    copyCamera.setViewport(this.screenWidth, this.screenHeight, left, bottom);

    const billboardCamera = this.getScenes()[2].camera;
    billboardCamera.setViewport(this.screenWidth, this.screenHeight, left, bottom);

    this.savedWidth = width;
    this.savedHeight = height;
  }


  update() {

    const mainScene = this.getMainScene();
    const copyScene = this.getSecondScene();
    const billboardScene = this.getScenes()[2];
    const mainCamera = mainScene.camera;

    //Note: supersampling
    const mainFramebuffer = this._initMainFramebuffer(this.screenWidth * 2, this.screenHeight * 2);
    const copyFramebuffer = this._initCopyFramebuffer();
    mainCamera.setViewport(this.screenWidth * 2, this.screenHeight * 2, 0, 0);

    if (extensions.isMultisamplingSupported()) {

      const mSamplingBuffer = this._initResolveFramebuffer(this.screenWidth * 2, this.screenHeight * 2);

      mainScene.setFramebuffer(mSamplingBuffer);
      mainScene.setResolvedBuffer(mainFramebuffer);

    } else {
      mainScene.setFramebuffer(mainFramebuffer);
    }

    copyScene.setFramebuffer(copyFramebuffer);
    billboardScene.setFramebuffer(copyFramebuffer);

    this.resize(this.savedWidth, this.savedHeight);

  }

  _initMainFramebuffer(width, height) {
    const copyScene = this.getSecondScene();

    const colorTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA, { width, height });
    const depthBuffer = resourceManager.initBuffer(pixelBufferType.RB_DEPTH, { width, height });
    const mainFramebuffer = new FrameBuffer([colorTexture], depthBuffer);

    const copyScreenPlane = this.getScreenQuad(copyScene);
    copyScreenPlane.setTexture(colorTexture, 0);

    return mainFramebuffer;

  }

  _initCopyFramebuffer() {
    const workspaceColor = this.attachmentsStorage[attachmentId.OUTPUT_COLOR_TEXTURE];
    return new FrameBuffer([workspaceColor], null);
  }

  _initResolveFramebuffer(width, height) {

    const samples = 4;
    const format = RBIntFormat.R8G8B8A8;
    const depthFormat = RBIntFormat.D16U;

    const mSamplingTexture = resourceManager.initBuffer(pixelBufferType.RB_RGBA,
        { width, height, format, samples });
    const mSamplingDepthBuffer = resourceManager.initBuffer(pixelBufferType.RB_DEPTH,
        { width, height, format: depthFormat, samples });

    return new FrameBuffer([mSamplingTexture], mSamplingDepthBuffer);

  }

}
